import { useContext, useState } from 'react';
import { useQuery } from '@apollo/client';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { Link } from 'react-router-dom';
import PageTemplate from '../components/PageTemplate';
import AppContext from '../services/context';
import ContributionsBadge from '../components/feedback/ContributionsBadge';
import { MY_FEEDBACKS } from '../graphql/feedback';
import { useError } from '../hooks/useError.js';

const TYPE_LABEL = {
  ERROR: 'Erro',
  IMPROVEMENT: 'Melhoria',
  CONTENT: 'Conteúdo',
  QUESTION: 'Dúvida',
};

const STATUS_LABEL = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  INVALIDATED: 'Invalidado',
  ANSWERED: 'Respondido',
};

const TIERS = [
  { count: 1,  label: '1 mês de Plus + badge Contribuidor' },
  { count: 5,  label: '4 meses totais de Plus' },
  { count: 10, label: '1 ano de Plus + selo Curador + 7 dias de trial Pro' },
  { count: 20, label: '2 anos de Plus + 30 dias de trial Pro' },
  { count: 30, label: '3 anos de Plus + 90 dias de trial Pro' },
  { count: 50, label: 'Plus vitalício + prioridade no beta Connect' },
];

const EarlyAccessNotice = () => {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4 space-y-2 text-sm">
      <p className="text-amber-900 dark:text-amber-100">
        Seu Plano Plus está limitado até o fim do Early Access (31/12/2026). Contribua com a plataforma e garanta mais
        benefícios — e até o Plus Ilimitado!{' '}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="underline font-medium text-amber-800 dark:text-amber-200 hover:no-underline focus:outline-none"
        >
          Saiba mais
        </button>
      </p>

      {open && (
        <div className="pt-1 space-y-3">
          <table className="hidden sm:table text-sm w-full border-collapse rounded-lg overflow-hidden border border-amber-200 dark:border-amber-700">
            <thead>
              <tr className="text-left bg-amber-100 dark:bg-amber-800/40 text-amber-900 dark:text-amber-100">
                <th className="py-2 px-3 font-semibold w-36 border-b border-amber-200 dark:border-amber-700">Contribuições</th>
                <th className="py-2 px-3 font-semibold border-b border-amber-200 dark:border-amber-700">Recompensa</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-100 dark:divide-amber-800/40">
              {TIERS.map((t) => (
                <tr key={t.count}>
                  <td className="py-2 px-3 font-medium">{t.count}</td>
                  <td className="py-2 px-3">{t.label}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <ul className="sm:hidden rounded-lg overflow-hidden border border-amber-200 dark:border-amber-700 divide-y divide-amber-100 dark:divide-amber-800/40">
            {TIERS.map((t) => (
              <li key={t.count} className="p-3 flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wide font-semibold text-amber-900 dark:text-amber-100 bg-amber-100 dark:bg-amber-800/40 self-start px-2 py-0.5 rounded">
                  {t.count} contribuiç{t.count === 1 ? 'ão' : 'ões'}
                </span>
                <span className="text-sm text-amber-900 dark:text-amber-100">{t.label}</span>
              </li>
            ))}
          </ul>

          <p className="text-xs text-amber-700 dark:text-amber-300">
            Fotos de plantas: 1 ponto por espécie, independente da quantidade de fotos enviadas. Exige declaração de autoria.
          </p>
        </div>
      )}
    </div>
  );
};

const MyContributions = () => {
  const { userState, themeKey } = useContext(AppContext);
  const { showError } = useError();
  usePageTitle('Minhas Contribuições');
  const isDarkTheme = themeKey === 'dark';
  const { data, loading } = useQuery(MY_FEEDBACKS, {
    skip: !userState.isAuthenticated,
    onError: (error) => showError(error, { fallback: 'Falha ao carregar suas contribuições.' }),
  });

  const feedbacks = data?.myFeedbacks || [];
  const contributions = feedbacks.filter((f) => f.type !== 'QUESTION');
  const questions = feedbacks.filter((f) => f.type === 'QUESTION');

  const showEarlyAccessNotice =
    userState.currentPlan === 'PLUS' &&
    !userState.plusEntitlementUntil &&
    !userState.plusLifetime;

  return (
    <PageTemplate showSearchInput={false} alwaysShowLogo>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <header className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold">Minhas contribuições</h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Tudo que você enviou — feedbacks que pontuam e dúvidas que respondemos.
            </p>
          </div>
          <ContributionsBadge
            count={userState.contributionCount}
            plusEntitlementUntil={userState.plusEntitlementUntil}
            plusLifetime={userState.plusLifetime}
            proTrialUntil={userState.proTrialUntil}
            isDarkTheme={isDarkTheme}
          />
        </header>

        {showEarlyAccessNotice && <EarlyAccessNotice />}

        {loading && <p className="text-sm">Carregando…</p>}

        <section>
          <h2 className="font-semibold text-lg mb-2">Contribuições (pontuam)</h2>
          {contributions.length === 0 && (
            <p className="text-sm text-gray-500">Nenhuma contribuição ainda. Use o botão Feedback no canto inferior direito.</p>
          )}
          <ul className="space-y-2">
            {contributions.map((f) => (
              <li
                key={f.id}
                className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 flex flex-col gap-1"
              >
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">{TYPE_LABEL[f.type]} · {STATUS_LABEL[f.status]}</span>
                  <span className="text-gray-500">
                    {f.countsTowardRewards ? '+1 ponto' : 'sem pontuação'}
                  </span>
                </div>
                <p className="font-semibold text-sm">{f.title}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">{f.body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="font-semibold text-lg mb-2">Dúvidas</h2>
          {questions.length === 0 && (
            <p className="text-sm text-gray-500">Você ainda não enviou dúvidas.</p>
          )}
          <ul className="space-y-2">
            {questions.map((f) => (
              <li
                key={f.id}
                className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 flex flex-col gap-1"
              >
                <div className="text-xs text-gray-500">{STATUS_LABEL[f.status]}</div>
                <p className="font-semibold text-sm">{f.title}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">{f.body}</p>
                {f.answer && (
                  <div className="mt-2 text-xs bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-2">
                    <strong>Resposta:</strong> {f.answer}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>

        <p className="text-xs text-gray-500">
          <Link to="/planos" className="underline">Veja os planos e a tabela de recompensas</Link>
        </p>
      </div>
    </PageTemplate>
  );
};

export default MyContributions;
